import Image from 'next/image'
import NextLink from 'next/link'
import clsx from 'clsx'

import type { ButtonProps } from '@wartek-id/button-v1'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import PlanningStatusBadge from 'app/KinerjaPenilikPegawaiPage/components/PlanningStatusBadge'

import type { StatusPerencanaan } from 'types/kinerja-penilik-pegawai'

interface PlanningCardLinkProps extends React.HTMLAttributes<HTMLDivElement> {
  href: string
}
const PlanningCardLink = ({ href, children }: PlanningCardLinkProps) => {
  if (Boolean(href)) {
    return (
      <NextLink href={href} passHref>
        {children}
      </NextLink>
    )
  }

  return <>{children}</>
}

interface PlanningCardProps extends React.HTMLAttributes<HTMLDivElement> {
  imageSrc: string
  title: string
  state?: StatusPerencanaan
  isDraft?: boolean
  href?: string
  onCtaClick?: () => void
  children?: React.ReactNode
}

const BUTTON_STATES: Record<
  StatusPerencanaan | 'DRAFT',
  { text: string; color: ButtonProps['color'] }
> = {
  MULAI_PERENCANAAN: { text: 'Mulai', color: 'black' },
  DRAFT: { text: 'Lanjutkan', color: 'blue' },
  SIAP_DIAJUKAN: { text: 'Ubah', color: 'white' },
  SEDANG_DIPERIKSA: { text: 'Cek', color: 'white' },
  DIARAHKAN_REVISI_TK: { text: 'Lakukan revisi', color: 'black' },
  DIARAHKAN_REVISI_PPK: { text: 'Lakukan revisi', color: 'black' },
  DIKIRIM_TK: { text: 'Cek', color: 'white' },
  DISETUJUI_PPK: { text: 'Cek', color: 'white' },
  PELAKSANAAN_DIMULAI: { text: 'Selengkapnya', color: 'blue' },
}

export default function PlanningCard({
  imageSrc,
  title,
  state,
  isDraft = false,
  href,
  onCtaClick = () => {},
  className,
  children,
  ...props
}: PlanningCardProps) {
  let buttonProps = BUTTON_STATES[isDraft ? 'DRAFT' : state]

  if (!!state && !buttonProps) {
    // This statement should be unreachable.
    // If it's reached, it means there's a new state that's not handled.

    console.error(`Invalid perencanaan state: ${state}`)

    buttonProps = { text: 'Cek', color: 'white' }
  }

  return (
    <div
      className={clsx(
        'px-6 py-3 flex gap-6 border-b last:border-b-0 border-b-subdued items-center',
        className
      )}
      {...props}
    >
      <Image
        src={imageSrc}
        width={64}
        height={64}
        alt=""
        className="select-none"
        draggable="false"
      />
      <div className="flex items-center justify-between flex-1 gap-1">
        <div className="flex flex-col gap-3">
          <Text variant="heading-sm">{title}</Text>
          {!!state && <PlanningStatusBadge state={state} />}
        </div>

        <div className="ml-auto">
          {!!children ? (
            children
          ) : (
            <PlanningCardLink href={href}>
              <Button size="md" color={buttonProps.color} onClick={onCtaClick}>
                {buttonProps.text}
              </Button>
            </PlanningCardLink>
          )}
        </div>
      </div>
    </div>
  )
}
